import {
  AVATAR_SIZE,
  PROXY_RADIUS,
  LOCATION_RADIUS,
  TEXT_OFFSET,
  SQUARE_SIZE,
  SHIELD_PATH,
  SERVICE_LINK_SIZE,
  SERVICE_TERMINAL_SIZE,
  EXPANDED_SIZE_MULTIPLIER,
} from './constants'
import * as d3 from 'd3'
import arrowIcon from 'lucide-static/icons/arrow-up-right.svg?raw'
import terminalIcon from 'lucide-static/icons/terminal.svg?raw'
import { parseLucideSvg } from '../utilities/lucide'
import { graphTooltip } from './tooltip'
import { drawCloudPlace, marksOf, marksSpacer } from './marks'
import { arcPath, decodeArcs } from './networkHues'

const LUCIDE_VIEWBOX_SIZE = 24
/** The ring of a node in the highlight (`arcs`), outside its glyph and its active outline. */
const ARC_RING_RADIUS = PROXY_RADIUS + 5

function registerLucideSymbol(
  defs: d3.Selection<SVGDefsElement, unknown, null, undefined>,
  id: string,
  svgString: string
) {
  const { viewBox, children } = parseLucideSvg(svgString)
  const finalViewBox = viewBox ?? `0 0 ${LUCIDE_VIEWBOX_SIZE} ${LUCIDE_VIEWBOX_SIZE}`
  const symbol = defs.append('symbol').attr('id', id).attr('viewBox', finalViewBox)

  children.forEach(child => {
    symbol.append(() => child.cloneNode(true) as SVGElement)
  })
}

export default class Renderer {
  private animationFrameId?: number
  private startTime?: number

  linkContainer: d3.Selection<SVGGElement, unknown, null, undefined>
  bridgeContainer: d3.Selection<SVGGElement, unknown, null, undefined>
  nodeContainer: d3.Selection<SVGGElement, unknown, null, undefined>
  labelContainer: d3.Selection<SVGGElement, unknown, null, undefined>
  particleContainer: d3.Selection<SVGGElement, unknown, null, undefined>

  constructor(container: d3.Selection<SVGGElement, unknown, null, undefined>) {
    this.linkContainer = container.append('g').attr('class', 'links')
    this.bridgeContainer = container.append('g').attr('class', 'bridges')
    this.particleContainer = container.append('g').attr('class', 'particles')
    this.nodeContainer = container.append('g').attr('class', 'nodes')
    this.labelContainer = container.append('g').attr('class', 'labels')
    const defs = container.append('defs')
    registerLucideSymbol(defs, 'lucide-link-2', arrowIcon)
    registerLucideSymbol(defs, 'lucide-terminal', terminalIcon)
  }

  node(d: GraphNode, group: d3.Selection<SVGGElement, unknown, null, undefined>) {
    group
      .attr('class', 'point')
      .classed(d.type, true)
      .classed('hidden', !!d.hidden)
      .classed('offline', d.offline)
      .classed('connected', d.connected)
      .classed('active', d.active)
      .classed('streaming', !!d.streaming)
      .classed('selected', !!d.selected)
      .classed('expanded', !!d.expanded)
      .classed('no-network', !!d.noNetwork)
    if (d.hue !== undefined) group.classed(`hue-${d.hue}`, true)

    const shape = this.shape(d, group)

    if (d.selected && shape) {
      // @ts-expect-error - D3 selection clone() method works on all SVG elements but TypeScript doesn't recognize this
      shape.clone(true).attr('class', 'outline').lower()
    }

    if (d.type === 'node') this.arcs(d.arcs, group, ARC_RING_RADIUS)

    // The hover text (app#339): "Node", and the compute text on a second line (app#329). A native
    // `<title>`, so it costs no pointer handling.
    // ⚠⚠ The ONLY `<title>` a node gets: a browser shows the first one and ignores the others.
    const tooltip = graphTooltip(d)
    // ⚠ Class `kind`, so the map finds this title by its class (`mapCore.ts`, the drag hint).
    if (tooltip) group.append('title').attr('class', 'kind').text(tooltip)
  }

  /**
   * The ring of a glyph in the highlight by network: one arc for each network (app#498). ⚠ A ring
   * and not a fill: the fill says online or offline.
   */
  arcs(text: string | undefined, group: d3.Selection<SVGGElement, unknown, null, undefined>, radius: number) {
    const arcs = decodeArcs(text)
    if (!arcs.length) return
    const ring = group.append('g').attr('class', 'arcs')
    arcs.forEach((hue, index) => {
      const whole = arcs.length === 1
      ring
        .append(whole ? 'circle' : 'path')
        .attr('r', whole ? radius : null)
        .attr('d', whole ? null : arcPath(index, arcs.length, radius))
        .attr('class', `hue-${hue}`)
    })
  }

  label(d: GraphNode, group: d3.Selection<SVGTextElement, unknown, null, undefined>) {
    group
      .attr('class', 'label')
      .attr('dy', d.type === 'account' ? AVATAR_SIZE * 0.9 : TEXT_OFFSET)
      .attr('text-anchor', 'middle')

    group.selectAll('*').remove()
    group.text(null)

    const label = d.label ?? ''
    const ranges = d.matchRanges
    if (ranges?.length) {
      let cursor = 0
      ranges.forEach(([start, end]) => {
        if (start > cursor) group.append('tspan').text(label.slice(cursor, start))
        group.append('tspan').attr('class', 'match').text(label.slice(start, end))
        cursor = end
      })
      if (cursor < label.length) group.append('tspan').text(label.slice(cursor))
    } else {
      group.text(label)
    }
    // The room for the marks after the name (nobgp-router#2010): `graph/marks.ts` draws them on it.
    const spacer = marksSpacer(marksOf(d))
    if (spacer) group.append('tspan').attr('class', 'marks-space').attr('aria-hidden', 'true').text(spacer)

    if (d.subLabel) {
      group
        .append('tspan')
        .attr('class', 'sub')
        .text(d.subLabel)
        .attr('x', 0)
        .attr('dy', '1.5em')
        .attr('text-anchor', 'middle')
    }

    group
      .classed(d.type, true)
      .classed('hidden', (d.hideLabel || !!d.hidden) && !d.active)
      .classed('offline', d.offline)
      .classed('active', d.active)
      .classed('selected', !!d.selected)
    // A network's chip on the map takes the classes of its name (`placeChips` in `mapCore.ts`).
    if (d.hue !== undefined) group.classed(`hue-${d.hue}`, true)
  }

  link(d: GraphLink, line: d3.Selection<SVGLineElement, unknown, null, undefined>) {
    line
      .attr('class', 'link')
      .classed('connected', !!d.connected)
      .classed('active', !!d.active)
      .classed('streaming', !!d.streaming)
      .classed('hidden', !!d.hidden)
    if (d.hue !== undefined) line.classed(`hue-${d.hue}`, true)
  }

  private renderLucideIcon(group: d3.Selection<SVGGElement, unknown, null, undefined>, symbolId: string) {
    const iconSize = SERVICE_TERMINAL_SIZE * EXPANDED_SIZE_MULTIPLIER * 0.8
    group
      .append('use')
      .attr('href', `#${symbolId}`)
      .attr('x', -iconSize / 2)
      .attr('y', -iconSize / 2)
      .attr('width', iconSize)
      .attr('height', iconSize)
  }

  private shape(d: GraphNode, group: d3.Selection<SVGGElement, unknown, null, undefined>) {
    switch (d.type) {
      case 'account': {
        const clip = group.append('g').attr('clip-path', 'circle(50%)').attr('class', 'avatar')

        const img = clip
          .append('image')
          .attr('href', d.url ?? '')
          .attr('width', AVATAR_SIZE)
          .attr('height', AVATAR_SIZE)
          .attr('x', -AVATAR_SIZE / 2)
          .attr('y', -AVATAR_SIZE / 2)
          .attr('preserveAspectRatio', 'xMidYMid slice')
          .attr('referrerPolicy', 'no-referrer')

        // Fallback if image fails
        img.on('error', () => {
          img.remove()
          const fallback = clip.append('g').attr('transform', `translate(${-AVATAR_SIZE / 2},${-AVATAR_SIZE / 2})`)
          fallback.append('rect').attr('width', AVATAR_SIZE).attr('height', AVATAR_SIZE)
          fallback
            .append('path')
            .attr(
              'd',
              'M44.774 50.9286V45.4931C44.774 42.6099 43.6287 39.8449 41.59 37.8062C39.5513 35.7674 36.7862 34.6221 33.903 34.6221H25.7498H17.5966C14.7134 34.6221 11.9483 35.7674 9.90962 37.8062C7.87092 39.8449 6.72559 42.6099 6.72559 45.4931V50.9286'
            )
          fallback
            .append('path')
            .attr(
              'd',
              'M25.7498 31.5424C31.7537 31.5424 36.6208 26.6753 36.6208 20.6714C36.6208 14.6675 31.7537 9.80042 25.7498 9.80042C19.7459 9.80042 14.8788 14.6675 14.8788 20.6714C14.8788 26.6753 19.7459 31.5424 25.7498 31.5424Z'
            )
        })
        return group
          .append('circle')
          .attr('r', AVATAR_SIZE / 2 + 1)
          .lower()
      }

      case 'node':
        group.append('circle').attr('r', PROXY_RADIUS)
        break

      // rounded triangle
      //  group.append('path').attr('d', ROUNDED_TRIANGLE_PATH).attr('transform', 'translate(-14, -12) scale(0.9)')

      // rounded diamond
      //   return group
      //     .append('rect')
      //     .attr('x', -10)
      //     .attr('y', -10)
      //     .attr('width', 20)
      //     .attr('height', 20)
      //     .attr('rx', 5)
      //     .attr('ry', 5)
      //     .attr('transform', 'rotate(45)')

      case 'location':
        // A cloud is drawn as its icon, large: it is not a place (`CloudPlace`).
        if (d.cloudPlace) drawCloudPlace(group, d.cloudPlace)
        else group.append('circle').attr('r', LOCATION_RADIUS)
        break

      case 'service':
        if (d.link) {
          if (d.expanded) {
            group.append('circle').attr('r', SERVICE_LINK_SIZE * EXPANDED_SIZE_MULTIPLIER)
            this.renderLucideIcon(group, 'lucide-link-2')
          } else {
            return group.append('circle').attr('r', SERVICE_LINK_SIZE)
          }
        } else {
          if (d.expanded) {
            group
              .append('rect')
              .attr('x', -((SERVICE_TERMINAL_SIZE * EXPANDED_SIZE_MULTIPLIER) / 2))
              .attr('y', -((SERVICE_TERMINAL_SIZE * EXPANDED_SIZE_MULTIPLIER) / 2))
              .attr('width', SERVICE_TERMINAL_SIZE * EXPANDED_SIZE_MULTIPLIER)
              .attr('height', SERVICE_TERMINAL_SIZE * EXPANDED_SIZE_MULTIPLIER)
              .attr('rx', 2)
              .attr('ry', 2)
            this.renderLucideIcon(group, 'lucide-terminal')
          } else {
            return group
              .append('rect')
              .attr('x', -SERVICE_TERMINAL_SIZE / 2)
              .attr('y', -SERVICE_TERMINAL_SIZE / 2)
              .attr('width', SERVICE_TERMINAL_SIZE)
              .attr('height', SERVICE_TERMINAL_SIZE)
          }
        }
        break

      case 'network':
        return group.append('path').attr('d', SHIELD_PATH).attr('transform', 'translate(-12, -12) scale(0.9)')

      default:
        group
          .append('rect')
          .attr('x', -SQUARE_SIZE / 2)
          .attr('y', -SQUARE_SIZE / 2)
          .attr('width', SQUARE_SIZE)
          .attr('height', SQUARE_SIZE)
          .attr('rx', SQUARE_SIZE / 4)
          .attr('ry', SQUARE_SIZE / 4)
    }

    group
      .append('circle')
      .attr('r', LOCATION_RADIUS + 12)
      .attr('fill', 'transparent')
      .attr('stroke', 'none')

    return null
  }

  startParticles() {
    if (!this.startTime) {
      this.startTime = Date.now()
    }

    if (!this.animationFrameId) {
      const animate = () => {
        const now = Date.now()
        const particles = this.particleContainer.selectAll<SVGCircleElement, GraphParticle>('.particle')

        particles.attr('transform', d => {
          const source = d.link.source as GraphNode
          const target = d.link.target as GraphNode

          if (!source?.x || !source?.y || !target?.x || !target?.y) return null

          // Calculate time in seconds
          const elapsed = now - this.startTime!
          const sec = elapsed / 1000

          // Calculate progress based on physical speed and distance
          const distanceTraveled = d.speed * sec
          const progress = (distanceTraveled / d.lineLength + d.progress) % 1

          // Apply reverse direction if needed
          const effectiveProgress = d.reverse ? progress : 1 - progress

          // Calculate position along the line
          const x = source.x + (target.x - source.x) * effectiveProgress
          const y = source.y + (target.y - source.y) * effectiveProgress

          return `translate(${x},${y})`
        })

        if (!particles.size()) {
          this.stopParticles()
          return
        }

        this.animationFrameId = requestAnimationFrame(animate)
      }

      this.animationFrameId = requestAnimationFrame(animate)
    }
  }

  stopParticles() {
    if (this.animationFrameId) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = undefined
      this.startTime = undefined
    }
  }
}
